import { useState } from 'react'
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useEventDebrief, useRecordMeetingOutcome, useAttachMeetingAsk } from '../../hooks/useLobbyDay'
import { RESPONSE_OPTIONS } from './debriefUtils'
import { groupRosterByOutcome } from './debriefRoster'
import { FollowUpRosterReveal } from './FollowUpRosterReveal'
import { AttachAskControl } from './AttachAskControl'
import type { AskResponse, AskTallyDto, MeetingDebriefRowDto } from '../../types/lobbyDay'

const TALLY_RESPONSES: { value: 'Yes' | 'No' | 'Maybe'; label: string; colorScheme: string }[] = [
  { value: 'Yes', label: 'Yes', colorScheme: 'green' },
  { value: 'No', label: 'No', colorScheme: 'red' },
  { value: 'Maybe', label: 'Maybe', colorScheme: 'yellow' },
]

interface OpenReveal { askId: string | null; askTitle: string; response: AskResponse }

function TallyBar({ tally, openReveal, onToggle }: {
  tally: AskTallyDto
  // When omitted, the Yes/No/Maybe counts render as static badges (used for the Overall row,
  // whose askId is null and would otherwise collide with the real "no ask assigned" per-ask row).
  openReveal?: OpenReveal | null
  onToggle?: (tally: AskTallyDto, response: AskResponse) => void
}) {
  const counts: Record<'Yes' | 'No' | 'Maybe', number> = { Yes: tally.yes, No: tally.no, Maybe: tally.maybe }
  const interactive = !!onToggle
  return (
    <HStack gap={3} flexWrap="wrap">
      <Text fontWeight="semibold">{tally.askTitle}</Text>
      {TALLY_RESPONSES.map((r) =>
        interactive ? (
          <Button
            key={r.value}
            size="sm"
            variant={openReveal?.askId === tally.askId && openReveal?.response === r.value ? 'solid' : 'plain'}
            colorPalette={r.colorScheme}
            minH={{ base: '44px', md: 'auto' }}
            onClick={() => onToggle!(tally, r.value)}
          >
            {counts[r.value]} {r.label}
          </Button>
        ) : (
          <Badge key={r.value} colorPalette={r.colorScheme}>{counts[r.value]} {r.label}</Badge>
        ),
      )}
      <Badge colorPalette="gray">{tally.notRecorded} not recorded</Badge>
      <Text color="gray.500" fontSize="sm">of {tally.total}</Text>
    </HStack>
  );
}

export function DebriefTab({ eventId }: { eventId: string }) {
  const { data, isLoading, error } = useEventDebrief(eventId)
  const record = useRecordMeetingOutcome(eventId)
  const attach = useAttachMeetingAsk(eventId)
  const [openReveal, setOpenReveal] = useState<OpenReveal | null>(null)

  const toggleReveal = (tally: AskTallyDto, response: AskResponse) =>
    setOpenReveal((prev) =>
      prev && prev.askId === tally.askId && prev.response === response
        ? null
        : { askId: tally.askId, askTitle: tally.askTitle, response },
    )

  const save = (row: MeetingDebriefRowDto, patch: { askResponse?: AskResponse; responseNotes?: string | null }) =>
    record.mutate({
      meetingId: row.meetingId,
      data: {
        askResponse: patch.askResponse ?? row.askResponse,
        responseNotes: patch.responseNotes !== undefined ? patch.responseNotes : row.responseNotes,
      },
    })

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (!!error || !data) return <Text color="red.500">Failed to load debrief.</Text>

  if (data.rows.length === 0) {
    return (
      <Text py={6} color="gray.500">
        No meetings scheduled yet — build the schedule first, then record how each legislator responded to the ask.
      </Text>
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      <Box bg="surface" p={4} borderRadius="lg" shadow="md">
        <Text fontWeight="bold" mb={2}>Ask outcomes</Text>
        <VStack align="stretch" gap={2}>
          <TallyBar tally={data.overall} />
          {data.tallies.map((t) => (
            <TallyBar key={t.askId ?? 'none'} tally={t} openReveal={openReveal} onToggle={toggleReveal} />
          ))}
        </VStack>
        {openReveal && (
          <FollowUpRosterReveal
            eventId={eventId}
            askId={openReveal.askId}
            askTitle={openReveal.askTitle}
            response={openReveal.response}
            roster={groupRosterByOutcome(data.rows, openReveal.askId, openReveal.response)}
          />
        )}
      </Box>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={3}>
        {data.rows.map((row) => (
          <Box key={row.meetingId} bg="surface" p={4} borderRadius="lg" shadow="md">
            <HStack justify="space-between" align="start">
              <Box>
                <Text fontWeight="semibold">
                  {row.legislatorName}
                  {row.party && <Text as="span" color="gray.500"> ({row.party[0]})</Text>}
                </Text>
                <Text fontSize="sm" color="gray.500" lineClamp={1}>{row.district}</Text>
              </Box>
              <Text fontSize="sm" color="gray.500">{row.startTime.slice(0, 5)}</Text>
            </HStack>

            <AttachAskControl
              meetingId={row.meetingId}
              askId={row.askId}
              askTitle={row.askTitle}
              askOptions={data.askOptions}
              onAttach={(askId) => attach.mutate({ meetingId: row.meetingId, askId })}
            />
            {row.attendeeNames.length > 0 && (
              <Text fontSize="xs" color="gray.500" lineClamp={1} mt={1}>
                with {row.attendeeNames.join(', ')}
              </Text>
            )}

            <Wrap gap={2} mt={3}>
              {RESPONSE_OPTIONS.map((o) => (
                <WrapItem key={o.value}>
                  <Button size="sm"
                    variant={row.askResponse === o.value ? 'solid' : 'outline'}
                    colorPalette={o.color}
                    onClick={() => save(row, { askResponse: o.value })}>
                    {o.label}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>

            <Input size="sm" mt={3} placeholder="Notes from the room…"
              defaultValue={row.responseNotes ?? ''}
              onBlur={(e) => {
                if (e.target.value !== (row.responseNotes ?? '')) save(row, { responseNotes: e.target.value || null })
              }} />
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );
}
